/**
 * Phase 4 — pattern library + real roster Brain tools.
 *
 * Five tools per `docs/scope/tactics-hub-roadmap.md` §Phase 4:
 *  - list_tactics_patterns      (read)  — filter the library by tags/phase/formation
 *  - recall_tactics_pattern     (read)  — fetch the full pattern (incl. layout) by id
 *  - save_tactics_pattern       (draft) — coach-approved write into the library
 *  - apply_tactics_pattern      (draft) — load a pattern's layout into the active board
 *  - apply_roster_to_layout     (draft) — map real squad to formation slots
 *
 * Read tools dispatch through `dispatchPatternTool` (called from the
 * foundation-owned `brainTools.ts` aggregator) so this phase's storage
 * access stays out of the central dispatcher. Draft tools follow the
 * established pattern: dispatcher returns `{ kind: 'draft', type, payload }`
 * and the surface-side commit pipeline handles the actual write.
 *
 * Layering: the storage layer (`apps/web2/src/lib/tacticsPatternsStore.ts`)
 * lives in the surface package, so this module can't import it directly —
 * `brainTools.ts` resolves the storage adapter through `BrainToolCtx`
 * and hands it to `dispatchPatternTool`. Apps register their adapter at
 * the dispatcher boundary via `ctx.getTacticsPatternStore?.()`.
 *
 * V1 storage is localStorage via `apps/web2/src/lib/tacticsPatternsStore.ts`;
 * v2 will be the `tactics_patterns` Prisma table per the roadmap.
 */
import type { AnthropicTool } from '@4cm/core/ai';
import type { TacticsLayout } from '@4cm/core/tactics';

// ---------------------------------------------------------------------------
// Storage adapter — apps register their concrete store at the dispatcher
// boundary. Keeps this module side-effect-free + free of apps/* imports.
// ---------------------------------------------------------------------------

export type TacticsPatternPhase =
  | 'attacking'
  | 'defending'
  | 'transitions'
  | 'individual';

export type TacticsPatternSource = 'PLATFORM' | 'CLUB' | 'CURATED_EXPERT';

export interface TacticsPatternRow {
  id: string;
  name: string;
  description?: string;
  tags: string[];
  phaseOfPlay: TacticsPatternPhase;
  formation: string;
  layout: TacticsLayout;
  sourceType: TacticsPatternSource;
  curatorName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TacticsPatternListFilter {
  tags?: string[];
  phaseOfPlay?: TacticsPatternPhase;
  formation?: string;
  limit?: number;
}

/**
 * Adapter that a surface package (apps/web2) provides at dispatch time.
 * Brain-side dispatcher is pure: no localStorage, no DOM — just calls
 * the adapter.
 */
export interface TacticsPatternStore {
  listPatterns(filter: TacticsPatternListFilter): TacticsPatternRow[];
  getPattern(id: string): TacticsPatternRow | null;
}

// ---------------------------------------------------------------------------
// Tool schemas
// ---------------------------------------------------------------------------

const PHASE_VALUES = ['attacking', 'defending', 'transitions', 'individual'] as const;

const LIST_TOOL: AnthropicTool = {
  name: 'list_tactics_patterns',
  description:
    'List saved tactics patterns from the library. Filter by phaseOfPlay, formation, ' +
    'or tags. Returns compact rows (id, name, tags, phase, formation, updatedAt) — ' +
    'NOT the full layout. Call recall_tactics_pattern when you need the geometry of a ' +
    'specific row.',
  input_schema: {
    type: 'object',
    properties: {
      tags: {
        type: 'array',
        items: { type: 'string' },
        description:
          'AND-match — patterns must carry every tag listed. Case-insensitive.',
      },
      phaseOfPlay: {
        type: 'string',
        description:
          'One of: attacking | defending | transitions | individual (primary phase taxonomy).',
      },
      formation: {
        type: 'string',
        description: 'Formation code, e.g. "4-3-3", "4-2-3-1".',
      },
      limit: {
        type: 'number',
        description: 'Cap on results. Default unlimited; recommended 20 for chat replies.',
      },
    },
  },
};

const RECALL_TOOL: AnthropicTool = {
  name: 'recall_tactics_pattern',
  description:
    'Fetch the full tactics pattern (including the TacticsLayout: spots, arrows, ' +
    'keyframes, branches, zones, coaching points) by id. Use after list_tactics_patterns ' +
    'when the coach picks a row to inspect, edit, or apply.',
  input_schema: {
    type: 'object',
    properties: {
      pattern_id: { type: 'string', description: 'Pattern id from list_tactics_patterns.' },
    },
    required: ['pattern_id'],
  },
};

const SAVE_TOOL: AnthropicTool = {
  name: 'save_tactics_pattern',
  description:
    'Propose saving the current board state as a named library pattern. Does NOT save — ' +
    'the coach must approve the draft card. Use when the coach asks to "save this", ' +
    '"remember this press", "name this pattern X", etc. If the layout is omitted, the ' +
    "surface fills it from the active board's persistKey on commit.",
  input_schema: {
    type: 'object',
    properties: {
      name: { type: 'string', description: 'Coach-friendly label, e.g. "Tuesday press".' },
      description: { type: 'string', description: 'Optional one-sentence intent.' },
      tags: {
        type: 'array',
        items: { type: 'string' },
        description: 'Free-form labels, e.g. ["high-press", "attacking", "4-3-3"].',
      },
      phaseOfPlay: {
        type: 'string',
        description: 'attacking | defending | transitions | individual.',
      },
      formation: { type: 'string', description: 'Formation code at save time.' },
      sourceType: {
        type: 'string',
        description:
          'PLATFORM | CLUB | CURATED_EXPERT. Defaults to CLUB. CURATED_EXPERT requires curatorName.',
      },
      curatorName: { type: 'string' },
      /**
       * Optional pre-snapshotted layout. Most calls omit this — the surface
       * reads the active board state on commit. Brain only sets this when
       * it composes a pattern wholesale rather than naming the current state.
       */
      layout: {
        type: 'object',
        description:
          'Optional. Pre-snapshotted TacticsLayout. Omit to capture the active board on commit.',
      },
    },
    required: ['name', 'phaseOfPlay', 'formation'],
  },
};

const APPLY_TOOL: AnthropicTool = {
  name: 'apply_tactics_pattern',
  description:
    "Propose loading a saved pattern's layout into the active board. Does NOT apply — " +
    'the coach must approve the draft card. Re-uses the Phase 1 tactics commit pipeline ' +
    'to write through saveTacticsLayout(persistKey, layout).',
  input_schema: {
    type: 'object',
    properties: {
      pattern_id: { type: 'string' },
      persist_key: {
        type: 'string',
        description: 'Target board. Defaults to the active board from screen context.',
      },
    },
    required: ['pattern_id'],
  },
};

const ROSTER_TOOL: AnthropicTool = {
  name: 'apply_roster_to_layout',
  description:
    "Propose binding real squad players to formation slots so the board's numbered " +
    'tokens become named tokens. Does NOT apply — coach approval required. The commit ' +
    'side handler (surface-level, out of scope here) takes the assignments and updates ' +
    'the roster overlay.',
  input_schema: {
    type: 'object',
    properties: {
      assignments: {
        type: 'array',
        description:
          'One entry per slot to bind. Slots are formation positions like "home-0" (GK) ' +
          'through "home-10". Players come from list_players (use the id).',
        items: {
          type: 'object',
          properties: {
            slot: {
              type: 'string',
              description: 'Slot key, e.g. "home-0", "home-7", "away-3".',
            },
            player_id: {
              type: 'string',
              description: 'Player UUID from list_players.',
            },
          },
          required: ['slot', 'player_id'],
        },
      },
    },
    required: ['assignments'],
  },
};

export const TACTICS_PATTERN_TOOLS: AnthropicTool[] = [
  LIST_TOOL,
  RECALL_TOOL,
  SAVE_TOOL,
  APPLY_TOOL,
  ROSTER_TOOL,
];

/** Public names — useful for assertions in tests. */
export const TACTICS_PATTERN_TOOL_NAMES = new Set<string>([
  'list_tactics_patterns',
  'recall_tactics_pattern',
  'save_tactics_pattern',
  'apply_tactics_pattern',
  'apply_roster_to_layout',
]);

// ---------------------------------------------------------------------------
// Dispatcher — called from brainTools.ts before the default-error branch
// ---------------------------------------------------------------------------

const READ_TOOL_NAMES = new Set(['list_tactics_patterns', 'recall_tactics_pattern']);
const DRAFT_TOOL_NAMES = new Set([
  'save_tactics_pattern',
  'apply_tactics_pattern',
  'apply_roster_to_layout',
]);

/**
 * Dispatch a Phase 4 pattern tool. Returns the result, or `null` when
 * `name` isn't one of ours (so the central dispatcher can keep looking).
 *
 * `store` is the surface-supplied adapter (apps/web2 passes its
 * localStorage-backed implementation). When the store is missing — e.g.
 * a server-side call — read tools return a structured error rather than
 * crashing so the model can recover.
 *
 * Draft tools return the standard `{ kind: 'draft', type, payload }`
 * envelope; the surface-side commit pipeline performs the actual write
 * (via `tacticsPatternsStore.savePattern` for save, the Phase 1 tactics
 * commit for apply, or a future roster handler for roster).
 */
export function dispatchPatternTool(
  name: string,
  input: unknown,
  store: TacticsPatternStore | null,
): unknown | null {
  if (READ_TOOL_NAMES.has(name)) {
    if (!store) {
      return {
        error:
          'tactics pattern library unavailable in this context (no storage adapter)',
      };
    }
    const arg = (input ?? {}) as Record<string, unknown>;
    if (name === 'list_tactics_patterns') {
      const filter: TacticsPatternListFilter = {};
      if (Array.isArray(arg.tags)) {
        filter.tags = (arg.tags as unknown[]).filter(
          (t): t is string => typeof t === 'string',
        );
      }
      if (typeof arg.phaseOfPlay === 'string' && isPhase(arg.phaseOfPlay)) {
        filter.phaseOfPlay = arg.phaseOfPlay;
      }
      if (typeof arg.formation === 'string') {
        filter.formation = arg.formation;
      }
      if (typeof arg.limit === 'number') {
        filter.limit = arg.limit;
      }
      const rows = store.listPatterns(filter);
      // Strip layout to keep response token spend low — model recalls
      // the full layout via recall_tactics_pattern when it needs detail.
      return rows.map((p) => ({
        id: p.id,
        name: p.name,
        description: p.description,
        tags: p.tags,
        phaseOfPlay: p.phaseOfPlay,
        formation: p.formation,
        sourceType: p.sourceType,
        curatorName: p.curatorName,
        updatedAt: p.updatedAt,
      }));
    }
    if (name === 'recall_tactics_pattern') {
      const id = typeof arg.pattern_id === 'string' ? arg.pattern_id : '';
      if (!id) return { error: 'pattern_id required' };
      const pattern = store.getPattern(id);
      if (!pattern) return { error: `pattern not found: ${id}` };
      return pattern;
    }
  }

  if (DRAFT_TOOL_NAMES.has(name)) {
    return { kind: 'draft', type: name, payload: { ...((input ?? {}) as object) } };
  }

  return null;
}

function isPhase(s: string): s is TacticsPatternPhase {
  return (PHASE_VALUES as readonly string[]).includes(s);
}
